import { useEffect, useState } from 'react';
import { NAV } from '../data/api';
import { LogoMark, signupUrl } from '@riskpay/ui-components';

/**
 * Scroll spy.
 *
 * IntersectionObserver over every section, keeping the topmost intersecting
 * heading marked active. `rootMargin` pulls the trigger line down below the
 * sticky header so a section highlights as its heading reaches the top of the
 * readable area, not when it first pokes into the viewport.
 */
function useActiveSection(ids: string[]): string {
  const [active, setActive] = useState(ids[0] ?? '');

  useEffect(() => {
    const visible = new Map<string, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.set(entry.target.id, entry.boundingClientRect.top);
          else visible.delete(entry.target.id);
        }
        if (visible.size === 0) return;
        const topmost = [...visible.entries()].sort((a, b) => a[1] - b[1])[0];
        if (topmost) setActive(topmost[0]);
      },
      { rootMargin: '-72px 0px -70% 0px', threshold: 0 },
    );

    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [ids.join('|')]);

  return active;
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const ids = NAV.flatMap((section) => section.items.map((item) => item.id));
  const active = useActiveSection(ids);

  return (
    <nav className="flex h-full flex-col" aria-label="Documentation">
      <div className="flex items-center gap-3 px-5 py-5">
        <LogoMark size={44} />
        <div className="flex flex-col leading-none">
          <span className="text-lg font-semibold tracking-tight">RiskPay</span>
          <span className="mt-1.5 text-xs text-content-subtle">Developer Docs</span>
        </div>
        <span className="badge badge-neutral ml-auto">v1</span>
      </div>

      <div className="scrollbar-thin flex-1 overflow-y-auto px-3 pb-8">
        {NAV.map((section) => (
          <div key={section.id} className="mb-6">
            <h2 className="px-2 py-1.5 text-2xs font-semibold uppercase tracking-[0.12em] text-content-faint">
              {section.label}
            </h2>
            <ul className="mt-1 space-y-0.5">
              {section.items.map((item) => {
                const isActive = item.id === active;
                return (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      onClick={onNavigate}
                      aria-current={isActive ? 'location' : undefined}
                      className={`block rounded-md px-2.5 py-2 text-[15px] transition-colors ${
                        isActive
                          ? 'bg-surface-overlay text-content shadow-[inset_2px_0_0_0_#10B981]'
                          : 'text-content-muted hover:bg-surface-overlay/60 hover:text-content'
                      }`}
                    >
                      {item.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-line px-5 py-4">
        <a href={signupUrl()} className="btn btn-primary w-full">
          Get API keys
        </a>
        <p className="mt-3 text-2xs leading-relaxed text-content-faint">
          Test keys are issued at signup — no approval needed to start building.
        </p>
      </div>
    </nav>
  );
}
